/**
 * Directory — single merged list of Suppliers + Subcontractors (Phase 2).
 * Learning: a prompt pops up for every bill whose supplier isn't yet known.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const BLANK = { name: "", kind: "supplier", trade: "", phone: "", email: "", address: "", nzbn: "", notes: "" };

export default function Directory() {
  const [rows, setRows] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("All");
  const [msg, setMsg] = useState("");
  const [learnFor, setLearnFor] = useState(null); // {bill_id, supplier}
  const [learnKind, setLearnKind] = useState("supplier");
  const [learnTrade, setLearnTrade] = useState("");

  async function load() {
    const [a, u] = await Promise.all([
      api.get("/directory"),
      api.get("/directory/unknown-bill-suppliers").catch(() => ({ data: [] })),
    ]);
    setRows(a.data || []); setUnknown(u.data || []);
  }
  useEffect(() => { load(); }, []);

  const visible = filter === "All" ? rows : rows.filter(r => r.kind === filter);

  async function add(e) {
    e.preventDefault();
    if (!form.name.trim()) { alert("Name is required"); return; }
    try {
      await api.post("/directory", form);
      setForm(BLANK); setMsg("Added ✓"); setTimeout(()=>setMsg(""), 2000); load();
    } catch (ex) { alert(ex?.response?.data?.detail || "Add failed"); }
  }
  async function save() {
    await api.patch(`/directory/${editing.id}`, editing);
    setEditing(null); setMsg("Saved ✓"); setTimeout(()=>setMsg(""), 2000); load();
  }
  async function del(r) {
    if (!window.confirm(`Delete "${r.name}"? Historical bills keep the name as free text.`)) return;
    await api.delete(`/directory/${r.id}`); load();
  }
  async function learnSave() {
    await api.post("/directory/learn-from-bill", {
      bill_id: learnFor.bill_id, kind: learnKind, trade: learnTrade,
    });
    setLearnFor(null); setLearnTrade(""); setLearnKind("supplier");
    setMsg("Remembered — future bills from this company will auto-sort ✓");
    setTimeout(()=>setMsg(""), 3000);
    load();
  }

  return (
    <div className="space-y-3" data-testid="directory-tab">
      {msg && <div className="text-green-400 text-sm">{msg}</div>}

      {unknown.length > 0 && (
        <div className="card border border-amber-400/60" data-testid="dir-unknown-panel">
          <h3 className="text-amber-300 text-sm font-bold">
            {unknown.length} bill supplier{unknown.length===1?"":"s"} to classify
          </h3>
          <p className="text-xs opacity-80 mb-2">Once you decide once, future bills from that company sort themselves into Materials or Subcontractors automatically.</p>
          <ul className="text-sm space-y-1">
            {unknown.slice(0, 8).map(u => (
              <li key={u.bill_id} className="flex items-center gap-2" data-testid={`dir-unknown-${u.bill_id}`}>
                <span className="flex-1"><b>{u.supplier}</b> <span className="opacity-60 text-xs">#{u.number||"—"}</span></span>
                <button className="hub-link text-xs"
                        onClick={()=>{ setLearnFor(u); setLearnKind("supplier"); setLearnTrade(""); }}
                        data-testid={`dir-classify-${u.bill_id}`}>Classify</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {learnFor && (
        <div className="card" data-testid="dir-learn-modal">
          <h3 className="text-silver mb-1">Classify <b>{learnFor.supplier}</b></h3>
          <div className="flex gap-2 items-end flex-wrap">
            <label className="text-sm">Kind
              <select className="input" value={learnKind} onChange={e=>setLearnKind(e.target.value)} data-testid="dir-learn-kind">
                <option value="supplier">Supplier (Materials)</option>
                <option value="subcontractor">Subcontractor</option>
              </select></label>
            <label className="text-sm">Trade
              <input className="input" placeholder="e.g. Plumber" value={learnTrade} onChange={e=>setLearnTrade(e.target.value)} data-testid="dir-learn-trade"/></label>
            <button className="btn-silver" onClick={learnSave} data-testid="btn-dir-learn-save">Remember</button>
            <button className="btn-ghost" onClick={()=>setLearnFor(null)}>Cancel</button>
          </div>
        </div>
      )}

      <form onSubmit={add} className="card grid grid-cols-1 md:grid-cols-6 gap-2">
        <input className="input" placeholder="Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="dir-name"/>
        <select className="input" value={form.kind} onChange={e=>setForm({...form,kind:e.target.value})} data-testid="dir-kind">
          <option value="supplier">Supplier</option>
          <option value="subcontractor">Subcontractor</option>
        </select>
        <input className="input" placeholder="Trade" value={form.trade} onChange={e=>setForm({...form,trade:e.target.value})} data-testid="dir-trade"/>
        <input className="input" placeholder="Phone" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/>
        <input className="input" placeholder="Email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
        <button className="btn-silver" data-testid="btn-dir-add">+ Add</button>
      </form>

      <div className="card">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <label className="text-sm">Filter
            <select className="input" value={filter} onChange={e=>setFilter(e.target.value)} data-testid="dir-filter">
              <option>All</option>
              <option value="supplier">Supplier</option>
              <option value="subcontractor">Subcontractor</option>
            </select>
          </label>
          <span className="text-xs opacity-70">{visible.length} entr{visible.length===1?"y":"ies"}</span>
        </div>
        <table className="table text-sm">
          <thead><tr><th>Name</th><th>Kind</th><th>Trade</th><th>Phone</th><th>Email</th><th>NZBN</th><th></th></tr></thead>
          <tbody>{visible.map(r => editing?.id === r.id ? (
            <tr key={r.id} data-testid={`dir-edit-${r.id}`}>
              <td><input className="input" value={editing.name||""} onChange={e=>setEditing({...editing,name:e.target.value})}/></td>
              <td><select className="input" value={editing.kind||"supplier"} onChange={e=>setEditing({...editing,kind:e.target.value})}>
                <option value="supplier">Supplier</option><option value="subcontractor">Subcontractor</option></select></td>
              <td><input className="input" value={editing.trade||""} onChange={e=>setEditing({...editing,trade:e.target.value})}/></td>
              <td><input className="input" value={editing.phone||""} onChange={e=>setEditing({...editing,phone:e.target.value})}/></td>
              <td><input className="input" value={editing.email||""} onChange={e=>setEditing({...editing,email:e.target.value})}/></td>
              <td><input className="input" value={editing.nzbn||""} onChange={e=>setEditing({...editing,nzbn:e.target.value})}/></td>
              <td className="flex gap-2 text-xs"><button className="hub-link" onClick={save} data-testid={`dir-save-${r.id}`}>Save</button><button onClick={()=>setEditing(null)}>Cancel</button></td>
            </tr>
          ) : (
            <tr key={r.id} data-testid={`dir-row-${r.id}`}>
              <td>{r.name}</td>
              <td>{r.kind === "subcontractor" ? <span className="badge badge-silver">Subcontractor</span> : "Supplier"}</td>
              <td>{r.trade || "—"}</td>
              <td>{r.phone || "—"}</td>
              <td>{r.email || "—"}</td>
              <td>{r.nzbn || "—"}</td>
              <td className="flex gap-2 text-xs">
                <button className="hub-link" onClick={()=>setEditing({...r})} data-testid={`btn-dir-edit-${r.id}`}>Edit</button>
                <button className="text-red-400" onClick={()=>del(r)}>Delete</button>
              </td>
            </tr>
          ))}
          {visible.length === 0 && <tr><td colSpan="7" className="opacity-50">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
